import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useJournalSettings } from '../hooks/useJournalSettings';
import { removeCanonical, setRobots } from '../hooks/useDocumentMeta';

const DESTINATIONS = [
  { to: '/articles', label: 'Browse articles', note: 'The current issue and every published article' },
  { to: '/journal-archives', label: 'Journal archive', note: 'Past volumes and issues' },
  { to: '/for-authors', label: 'For authors', note: 'Preparing and submitting a manuscript' },
  { to: '/contact', label: 'Contact the journal', note: 'If you followed a link that should work' },
];

/**
 * Any address that isn't a page of the site. Tells search engines not to index it
 * (noindex, no canonical) -- the static host answers these addresses with the app shell,
 * so this page is what a crawler sees.
 */
const NotFound = () => {
  const settings = useJournalSettings();
  const journal = settings.journalAbbreviation || settings.journalName;

  useEffect(() => {
    removeCanonical();
    setRobots('noindex, nofollow');
    if (!settings.settingsLoading) document.title = `Page not found | ${journal}`;
  }, [journal, settings.settingsLoading]);

  return (
    <div className="bg-vellum">
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-24">
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.12em] text-brick">Error 404</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-espresso mt-3">Page not found</h1>
        <p className="text-ink-2 text-lg leading-relaxed mt-4">
          There is no page at this address. It may have been moved, or the link may be mistyped.
        </p>
        <ul className="mt-10 divide-y divide-dust border-y border-dust">
          {DESTINATIONS.map((d) => (
            <li key={d.to}>
              <Link to={d.to} className="group flex items-center justify-between gap-4 py-4 hover:text-brick">
                <span>
                  <span className="block font-display text-lg font-semibold text-espresso group-hover:text-brick">{d.label}</span>
                  <span className="block text-sm text-ink-2">{d.note}</span>
                </span>
                <ArrowRight className="h-5 w-5 text-brick flex-shrink-0" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
        <p className="mt-8 text-ink-2">
          Or return to the <Link to="/" className="text-brick font-semibold hover:underline">home page</Link>.
        </p>
      </section>
    </div>
  );
};

export default NotFound;
